// Moving my otter: keys (with sliding along walls, slipping into doors, and pushing off the deck into a boat or out
// of one onto land), click-to-travel (walk, board, sail, land, walk), and what goes out to the office: a position at
// most every 100 ms while moving, and always the spot where I stop. No DOM and no renderer: the movement tests drive
// it in Node and replay what it sends through the office's own rules (src/rules.ts).
// The office runs it too: src/office/walk.ts walks agents' otters with it, so keep it free of DOM and browser-only APIs.
import { collider, WALK_SPEED, BOAT_SPEED } from './collide.js';
import { findPath, boxFits, sweepClear, nearestTile, escapeClear, recoveryPositions, initializeNavigation, NavigationUnavailable, NavigationCancelled, NavigationInvalidated, navigationState } from './nav.js';

const SPEED = WALK_SPEED;
/** The pseudo key the on-screen stick holds down in the mover's keys while it is pushed (see setStick). */
export const STICK = 'stick';
// Keys alone also cross the water: push off the deck's edge into the sea for a moment and you're in a boat;
// push a boat against the deck or an island and you climb out. Going in takes a short hold (walking up to the edge
// isn't jumping in); climbing out happens the moment you swim into the shore, so the otter doesn't stall against it.
// A cooldown keeps it from flapping.
const PUSH_IN_MS = 90, PUSH_OUT_MS = 0, BOAT_COOLDOWN_MS = 500;
/** How long the drawn otter takes to glide from the water's edge onto the deck (or in): the position itself hops
 *  (that's what the office checks), what's drawn eases across (deck/pose.js hopGlide; the camera follows that). */
export const HOP_MS = 200;

// Facing. `dir` is the 4-way one everything has always used (0 down, 1 right, 2 up, 3 left) and still goes on the
// wire; `dir8` is the 8-way one the otters are drawn with: 0 down, 1 down-right, 2 right, 3 up-right, 4 up, 5 up-left,
// 6 left, 7 down-left (so dir8 = 2 * dir on the straights). Something that only knows `dir` gets dir8 = 2 * dir.
/** The 4-way facing of a movement vector (y down), as it always was: the bigger axis wins, a tie goes up / down. */
export const dir4Of = (dx, dy) => (Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 1 : 3) : dy > 0 ? 0 : 2);
/** The 8-way facing of a movement vector (y down). prev: the facing now, kept until the vector is clearly past the
 *  next one's 45-degree sector (no flicker on a path that runs along a sector's edge). */
export function dir8Of(dx, dy, prev = -1) {
  const a = Math.atan2(dx, dy); // 0 = down, pi/2 = right
  const d = (((Math.round(a / (Math.PI / 4)) % 8) + 8) % 8);
  if (prev >= 0 && prev < 8 && prev !== d) {
    let off = Math.abs(a - prev * (Math.PI / 4)) % (2 * Math.PI); if (off > Math.PI) off = 2 * Math.PI - off;
    if (off < Math.PI / 8 + 0.14) return prev;
  }
  return d;
}
/** An otter's 8-way facing: its own, or else its 4-way one's. */
export const dir8 = (p) => (Number.isInteger(p?.dir8) && p.dir8 >= 0 && p.dir8 < 8 ? p.dir8 : ((p?.dir ?? 0) & 3) * 2);
// `dir16`: the 16-way facing the 3D otters are drawn with (0 down, then clockwise on screen: 4 right, 8 up, 12 left),
// sent alongside dir / dir8 by newer clients. It's only believed while it agrees with dir8 (within one 16th), so
// anything that turns an otter by dir8 alone (a game seat, the fishing spot, an older client) is drawn that way.
/** The 16-way facing of a movement vector (y down), with the same hysteresis as dir8Of (half a sector more). */
export function dir16Of(dx, dy, prev = -1) {
  const a = Math.atan2(dx, dy), S = Math.PI / 8, d = ((Math.round(a / S) % 16) + 16) % 16;
  if (prev >= 0 && prev < 16 && prev !== d) {
    let off = Math.abs(a - prev * S) % (2 * Math.PI); if (off > Math.PI) off = 2 * Math.PI - off;
    if (off < S / 2 + 0.07) return prev;
  }
  return d;
}
const near16 = (a, b) => { const d = Math.abs(a - b) % 16; return Math.min(d, 16 - d) <= 1; };
/** An otter's 16-way facing: its own while it agrees with its dir8, or else its dir8's. */
export const dir16 = (p) => (Number.isInteger(p?.dir16) && p.dir16 >= 0 && p.dir16 < 16 && near16(p.dir16, dir8(p) * 2) ? p.dir16 : dir8(p) * 2);

/**
 * @param {object} o
 * @param {object} o.MAP the map (deck/map.js)
 * @param {() => object|null} o.me my otter ({x, y, dir, moving, boat}); moved in place (and `running`: on a
 *   click-to-go trip, not by keys; only drawn, never sent)
 * @param {(m: object) => void} o.send to the office
 * @param {(text: string) => void} [o.toast]
 * @param {() => void} [o.onNavigationUnavailable] planner initialization, resource, or query failure
 * @param {() => number} [o.now] ms clock (performance.now)
 * @param {Set<string>} [o.keys] keys held (lower-case KeyboardEvent.key)
 * @param {(x0: number, y0: number, x: number, y: number) => boolean} [o.mayEnter] may this frame's step go from (x0, y0)
 *   to (x, y)? false: it isn't taken (a 会议室 while in a 通话, public/call.js)
 */
// t: the page's translator, word() here (/i18n.js t; this module also runs in the office and in Node, where nobody reads the toasts)
export function createMover({ MAP, me: getMe, send, toast = () => {}, onNavigationUnavailable = () => {}, t: word = (k) => k, now = () => performance.now(), keys = new Set(), mayEnter = () => true }) {
  // Where the body fits and what each tile is: the grid collider (collide.js) until a renderer brings its own with
  // the same interface (the Phaser one answers with Arcade Physics against the Tiled layers: deck/tilemap.js).
  let C = collider(MAP);
  const water = (x, y) => C.water(x, y), footOk = (x, y) => C.footOk(x, y);
  let me = null; // the otter being moved, for the length of a call (getMe() may be a new object after a reconnect)
  const boxFree = (x, y) => C.boxFree(x, y, !!me?.boat);
  let path = null, plan = [], lastSent = 0, recovering = false, planning = false, generation = 0, intent = null, trips = 0;
  let aim = null, replans = 0; // where the path in hand goes (walkTo's arguments), and how often it was planned again
  let boatAt = -1e9, push = null; // when I last got in/out; {key, since} while keys push into something
  let sentAt = { x: NaN, y: NaN, moving: false, boat: false, dir: -1, dir8: -1, dir16: -1 }; // the last position I told the office
  // The on-screen stick (phones, deck/stick.js): a direction and how far it's pushed (0..1 of walking speed). It counts
  // only while `keys` holds STICK, so whatever stops the walking keys (keys.clear(): chat opening, a game, the tab
  // hidden) stops the stick too, and what checks keys.size (a pending sit, pick.js) sees it as walking by keys.
  let stick = null;
  const withMe = (f) => (...a) => { me = getMe(); if (!me) return undefined; return f(...a); };
  const active = (ctx) => !!ctx && intent === ctx && generation === ctx.id && !ctx.controller.signal.aborted && getMe() === ctx.actor;
  const guard = (ctx) => {
    if (!active(ctx)) throw new NavigationCancelled();
    if (ctx.runningRevision !== ctx.revision) throw new NavigationInvalidated();
    me = ctx.actor;
  };
  const pause = () => new Promise((resolve) => setTimeout(resolve, 0));
  function cancelIntent() { generation++; intent?.controller.abort(); intent = null; planning = false; }
  function failed(error) {
    if (error instanceof NavigationCancelled) return;
    if (!(error instanceof NavigationUnavailable)) console.error('Holt navigation failed:', error);
    path = null; plan = []; recovering = false; onNavigationUnavailable(error);
    toast(word(navigationState() === 'loading' ? 'map.move.loading' : 'map.move.unavailable'));
  }
  async function run(ctx, operation) {
    try {
      if (!(await initializeNavigation())) throw new NavigationUnavailable();
      for (;;) {
        ctx.runningRevision = ctx.revision;
        guard(ctx);
        try { const result = await operation(); guard(ctx); return result; }
        catch (error) {
          if (active(ctx) && (error instanceof NavigationInvalidated ||
              (error instanceof NavigationCancelled && ctx.runningRevision !== ctx.revision))) { path = null; plan = []; continue; }
          throw error;
        }
      }
    } catch (error) {
      if (!active(ctx) || error instanceof NavigationCancelled) return null;
      failed(error); return false;
    }
  }
  function beginOperation(ctx, operation) {
    planning = true;
    const promise = run(ctx, operation).finally(() => { if (intent === ctx) planning = false; });
    ctx.promise = promise; return promise;
  }
  function startIntent(kind, args, count = true) {
    const actor = getMe(); if (!actor) return Promise.resolve(false);
    cancelIntent(); path = null; plan = []; recovering = false;
    const ctx = { id: generation, actor, controller: new AbortController(), kind, args, revision: 0, runningRevision: 0, promise: null };
    intent = ctx; if (count && kind === 'travel') trips++;
    const operation = () => kind === 'travel' ? travelTo(...args, ctx) : walkTo(...args, ctx);
    return beginOperation(ctx, operation);
  }
  function continueIntent(operation) {
    const ctx = intent; if (!active(ctx) || planning) return;
    let restart = false;
    void beginOperation(ctx, async () => {
      try { return await (restart ? (ctx.kind === 'travel' ? travelTo(...ctx.args, ctx) : walkTo(...ctx.args, ctx)) : operation(ctx)); }
      catch (error) {
        if (!active(ctx) || !(error instanceof NavigationInvalidated ||
            (error instanceof NavigationCancelled && ctx.runningRevision !== ctx.revision))) throw error;
        path = null; plan = []; restart = true;
        throw error;
      }
    });
  }

  function sendPos(moving) {
    sentAt = { x: +me.x.toFixed(2), y: +me.y.toFixed(2), moving, boat: !!me.boat, dir: me.dir, dir8: dir8(me), dir16: dir16(me) };
    send({ t: 'pos', x: sentAt.x, y: sentAt.y, dir: me.dir, dir8: sentAt.dir8, dir16: sentAt.dir16, moving, boat: sentAt.boat });
  }
  /** Into a boat from the deck's edge, or ashore from the sea next to the deck (travel plans use this). Going ashore
   *  with somewhere to go (`toward`, a tile), onto the next tile closest to it, so a click on a beach lands on it. */
  function toggleBoat(toward, moving = false) {
    const fx = Math.floor(me.x + 0.5), fy = Math.floor(me.y + 0.8);
    const ok = ([x, y]) => (me.boat ? footOk(x, y) : water(x, y)), from = (p) => (q) => Math.hypot(q[0] - p[0], q[1] - p[1]);
    if (me.boat && toward) {
      const next = [];
      const wall = (x, y) => !footOk(x, y) && !water(x, y); // a diagonal hop may not brush past a wall's corner
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
        if ((dx || dy) && ok([fx + dx, fy + dy]) && !(dx && dy && (wall(fx + dx, fy) || wall(fx, fy + dy)))) next.push([fx + dx, fy + dy]);
      }
      const d = from(toward), h = from([fx, fy]);
      next.sort((a, b) => d(a) - d(b) || h(a) - h(b));
      if (next.length) return boatSwitch(next[0], moving);
    }
    const near = [];
    for (let r = 1; r <= 2; r++) for (let dy = -r; dy <= r; dy++) for (let dx = -r; dx <= r; dx++) near.push([fx + dx, fy + dy]);
    near.sort((a, b) => from([fx, fy])(a) - from([fx, fy])(b));
    const spot = near.find(ok);
    if (spot) boatSwitch(spot, moving);
  }
  /** Step into the boat at a water tile, or out of it onto a deck tile, and tell everyone. The hop is as short as it
   *  can be: to the nearest spot on that tile where the body fits (keys: straight across the edge, a third of a tile,
   *  not sideways to the tile's middle), and drawn as a glide from where I was (me.hop, deck/pose.js hopGlide).
   *  moving: still going (keys held, a trip going on), so the others keep seeing me swim / walk, not stop. */
  function boatSwitch(spot, moving = false) {
    // first where I stand now (the last move may still be inside the send throttle), then the hop
    if (+me.x.toFixed(2) !== sentAt.x || +me.y.toFixed(2) !== sentAt.y) sendPos(moving);
    const to = landingIn(spot, !me.boat) ?? [spot[0], spot[1] - 0.3], t = now();
    me.hop = { x: me.x, y: me.y, at: t };
    me.boat = !me.boat; me.x = to[0]; me.y = to[1]; path = null; recovering = false; boatAt = t;
    sendPos(moving); lastSent = t;
  }
  /** The position with the feet on tile `spot` nearest to where I am now, where the body fits in (boat) or on foot. */
  function landingIn([tx, ty], boat) {
    let best = null, bd = Infinity;
    // feet = floor(x + 0.5), floor(y + 0.8): x in [tx - 0.5, tx + 0.5), y in [ty - 0.8, ty + 0.2); in 0.05-tile cells,
    // each tried at its point nearest to me, already rounded the way positions are sent (2 decimals), so what's
    // checked here is what the office gets
    const near = (v, lo) => +Math.min(Math.max(v, lo), lo + 0.04).toFixed(2);
    for (let j = 0; j < 20; j++) for (let i = 0; i < 20; i++) {
      const x = near(me.x, tx - 0.5 + i * 0.05), y = near(me.y, ty - 0.8 + j * 0.05), d = Math.hypot(x - me.x, y - me.y);
      if (d < bd && Math.floor(x + 0.5) === tx && Math.floor(y + 0.8) === ty && C.boxFree(x, y, boat)) { bd = d; best = [x, y]; }
    }
    return best;
  }
  // Going somewhere: walk if you can; otherwise walk to the shore, take a boat, sail to the shore nearest the target,
  // land and walk the rest. No keys needed. The shore it boards at and the one it lands at are on the same water as
  // each other, and the landing is on the target's own land (a pond painted in the map editor, or a room with no door,
  // isn't somewhere to sail from or to).
  const SIDES = [[1, 0], [-1, 0], [0, 1], [0, -1]];
  /** Tiles side by side from `seeds` where ok: {seen: their keys, list: them, nearest the seeds first}. */
  const key = (x, y) => `${x},${y}`;
  async function spread(seeds, ok, ctx) {
    const seen = new Set(), q = [];
    for (const [x, y] of seeds) if (ok(x, y) && !seen.has(key(x, y))) { seen.add(key(x, y)); q.push([x, y]); }
    for (let h = 0; h < q.length; h++) {
      if (h && h % 2048 === 0) { await pause(); guard(ctx); }
      const [x, y] = q[h];
      for (const [dx, dy] of SIDES) { const nx = x + dx, ny = y + dy, k = key(nx, ny); if (!seen.has(k) && ok(nx, ny)) { seen.add(k); q.push([nx, ny]); } }
    }
    return { seen, list: q };
  }
  /** Disjoint 4-connected foot tiles prove that two fitting bodies cannot walk between the points: the feet lie
   * inside the body, and its positive width and height prevent crossing diagonally between two blocked tiles.
   * Search both ends so nearby routes stop quickly; an exhausted component can be reused by the boat planner. */
  async function connectedFoot(start, finish, ctx) {
    const a = { seen: new Set([key(...start)]), list: [start], head: 0 };
    const b = { seen: new Set([key(...finish)]), list: [finish], head: 0 };
    if (a.seen.has(key(...finish))) return { connected: true };
    let count = 0;
    while (a.head < a.list.length && b.head < b.list.length) {
      if (count && count % 2048 === 0) { await pause(); guard(ctx); }
      const current = a.list.length - a.head <= b.list.length - b.head ? a : b, other = current === a ? b : a;
      const [x, y] = current.list[current.head++]; count++;
      for (const [dx, dy] of SIDES) {
        const nx = x + dx, ny = y + dy, k = key(nx, ny);
        if (current.seen.has(k) || !footOk(nx, ny)) continue;
        if (other.seen.has(k)) return { connected: true };
        current.seen.add(k); current.list.push([nx, ny]);
      }
    }
    return { connected: false, mine: a.head === a.list.length ? a : null, land: b.head === b.list.length ? b : null };
  }
  /** How to get to tile (tx, ty) by boat: {edge, board} (on foot: the deck tile to walk to and the water tile to step
   *  into) and {land} (the water tile to sail to: the target itself if it's water, else next to its land), or null. */
  async function seaRoute(tx, ty, wantWater, exact, ctx, known = {}) {
    const fx = Math.floor(me.x + 0.5), fy = Math.floor(me.y + 0.8);
    let goals;
    if (wantWater) goals = [[tx, ty]];
    else {
      const seeds = exact ? [[tx, ty]] : [-1, 0, 1].flatMap((dy) => [-1, 0, 1].map((dx) => [tx + dx, ty + dy]));
      goals = [];
      const land = known.land ?? await spread(seeds, footOk, ctx), got = new Set(); guard(ctx);
      for (const [x, y] of land.list) for (const [dx, dy] of SIDES) { const k = key(x + dx, y + dy); if (!got.has(k) && water(x + dx, y + dy)) { got.add(k); goals.push([x + dx, y + dy]); } }
    }
    if (!goals.length) return null;
    // the bodies of water the goals are on, each spread once
    const sea = new Map(); // tile key -> which water
    for (const [x, y] of goals) if (!sea.has(key(x, y))) { const id = sea.size; const found = await spread([[x, y]], water, ctx); guard(ctx); for (const k of found.seen) sea.set(k, id); }
    const nearest = (id) => { let best = null, bd = Infinity; for (const g of goals) { const d = Math.hypot(g[0] - tx, g[1] - ty); if (sea.get(key(...g)) === id && d < bd) { bd = d; best = g; } } return best; };
    if (me.boat) { const id = sea.get(key(fx, fy)); return id === undefined ? null : { land: nearest(id) }; }
    // on foot: the nearest bit of my deck (by steps) beside one of those waters
    const mine = known.mine ?? await spread([[fx, fy]], footOk, ctx); guard(ctx);
    for (const [x, y] of mine.list) for (const [dx, dy] of SIDES) {
      const id = sea.get(key(x + dx, y + dy));
      if (id !== undefined) return { edge: [x, y], board: [x + dx, y + dy], land: nearest(id) };
    }
    return null;
  }
  /** nearest: if it can't get there at all, go as near as it can (a click); else just say so. */
  async function travelTo(tx, ty, exact, finalPoint, nearest = false, ctx) {
    guard(ctx); path = null; plan = [];
    // Boat routing yields while exploring both water and land. Detect edits even if an embedding forgot to call replan.
    const mapStamp = () => `${MAP.x0 ?? 0},${MAP.y0 ?? 0},${MAP.width},${MAP.height}|${MAP.solid.join('')}|${MAP.water.join('')}`;
    const stamp = mapStamp();
    const wantWater = water(tx, ty) && !footOk(tx, ty);
    let known = {}, skipDirect = false;
    // A same-mode exact click on a different island cannot have a land path. Avoid building two full land meshes
    // to discover that fact, but only after a tile-connectivity proof; an unobstructed click remains immediate.
    if (!me.boat && !wantWater && exact && boxFits(C.solid, me.x, me.y, false)) {
      const goal = walkGoal(tx, ty, exact, finalPoint, 1, false);
      if (goal.x !== undefined && [goal.x, goal.y].every(Number.isFinite) && boxFits(C.solid, goal.x, goal.y, false) &&
          !sweepClear(C.solid, me.x, me.y, goal.x, goal.y, false, 0)) {
        const start = [Math.floor(me.x + 0.5), Math.floor(me.y + 0.8)];
        const finish = [Math.floor(goal.x + 0.5), Math.floor(goal.y + 0.8)];
        if (footOk(...start) && footOk(...finish)) {
          const result = await connectedFoot(start, finish, ctx); guard(ctx);
          if (mapStamp() !== stamp) throw new NavigationInvalidated();
          skipDirect = !result.connected;
          if (skipDirect) known = { mine: result.mine, land: finish[0] === tx && finish[1] === ty ? result.land : null };
        }
      }
    }
    // 1) direct only when destination and current mode agree: changing between land and water needs a shore route.
    if (!!me.boat === wantWater && !skipDirect) {
      await walkTo(tx, ty, exact, finalPoint, 1, ctx); guard(ctx);
      if (path) return true;
    }
    // 2) by boat: on foot, walk to the shore and board; then sail, and (for land) land and walk the rest
    const route = await seaRoute(tx, ty, wantWater, exact, ctx, known); guard(ctx);
    if (mapStamp() !== stamp) throw new NavigationInvalidated();
    if (route) {
      if (route.edge) plan.push({ do: 'walk', x: route.edge[0], y: route.edge[1], exact: true }, { do: 'board', at: route.board });
      if (wantWater) plan.push({ do: 'sail', x: tx, y: ty, finalPoint });
      else plan.push({ do: 'sail', x: route.land[0], y: route.land[1] }, { do: 'land', toward: [tx, ty] }, { do: 'walk', x: tx, y: ty, exact, finalPoint });
      return nextLeg(ctx);
    }
    // 3) no way there at all (inside a room with no door, the middle of a wall, a pond inside one): as near as it gets
    if (nearest) {
      const near = await nearestTile({ solid: C.solid, map: MAP, boat: !!me.boat, signal: ctx.controller.signal }, me.x, me.y, tx, ty); guard(ctx);
      if (near) { await walkTo(near[0], near[1], false, null, 0, ctx); guard(ctx); if (path) return true; }
    }
    toast(word('map.move.unreachable'));
    return false;
  }
  async function nextLeg(ctx) {
    guard(ctx);
    while (plan.length && !path) {
      const leg = plan.shift();
      if (leg.do === 'board') {
        if (me.boat) continue;
        const [fx, fy] = [Math.floor(me.x + 0.5), Math.floor(me.y + 0.8)];
        // into the water the route sails on (right beside me), else whatever water is nearest
        if (leg.at && Math.abs(leg.at[0] - fx) + Math.abs(leg.at[1] - fy) === 1 && water(...leg.at)) boatSwitch(leg.at, plan.length > 0);
        else toggleBoat(undefined, plan.length > 0);
      }
      else if (leg.do === 'land') { if (me.boat) toggleBoat(leg.toward, plan.length > 0); }
      else {
        await walkTo(leg.x, leg.y, leg.do === 'sail' ? true : leg.exact, leg.finalPoint ?? null, 1, ctx); guard(ctx);
        if (!path) { toast(word('map.move.blocked')); plan = []; return false; }
      }
    }
    return true;
  }
  /** The way to tile (tx, ty), in what I'm in now (nav.js: for the body box, any angle, shortest): `point` given,
   *  ending exactly on that position (else as near it as the body fits, with the feet on the same tile); exact,
   *  on the tile's own position; otherwise anywhere with the feet within r tiles of it (0: on it). Sets `path`
   *  (null: no way). */
  async function walkTo(tx, ty, exact, point, r = 1, ctx) {
    guard(ctx); path = null; recovering = false;
    const boat = !!me.boat, nav = { solid: C.solid, map: MAP, boat };
    const goal = walkGoal(tx, ty, exact, point, r, boat);
    const fits = boxFits(C.solid, me.x, me.y, boat);
    const starts = fits ? [[me.x, me.y]] : recoveryPositions(C.solid, me.x, me.y, boat);
    for (const [x, y] of starts) {
      const way = await findPath({ ...nav, signal: ctx.controller.signal }, x, y, goal); guard(ctx);
      if (!way) continue;
      path = fits ? way : [[x, y], ...way]; recovering = !fits;
      aim = { tx, ty, exact, point: goal.x !== undefined ? [goal.x, goal.y] : null, r }; replans = 0; stuck = 0; return true;
    }
    return false;
  }
  function walkGoal(tx, ty, exact, point, r, boat) {
    let goal = exact ? { x: tx, y: ty } : { tx, ty, r };
    if (exact && point) goal = fitNear(point[0], point[1], boat) ?? (C.solid(tx, ty, boat) ? { tx, ty, r } : { x: tx, y: ty });
    if (goal.x !== undefined && !boxFits(C.solid, goal.x, goal.y, boat)) goal = { tx, ty, r };
    return goal;
  }
  /** The nearest position to (x, y) where the body fits with the feet on the same tile (a click right by a wall). */
  function fitNear(x, y, boat) {
    if (boxFits(C.solid, x, y, boat)) return { x, y };
    const [fx, fy] = [Math.floor(x + 0.5), Math.floor(y + 0.8)];
    let best = null, bd = Infinity;
    for (let j = -20; j <= 20; j++) for (let i = -20; i <= 20; i++) {
      const cx = x + i * 0.025, cy = y + j * 0.025, d = Math.hypot(i, j);
      if (d < bd && Math.floor(cx + 0.5) === fx && Math.floor(cy + 0.8) === fy && boxFits(C.solid, cx, cy, boat)) { bd = d; best = { x: +cx.toFixed(3), y: +cy.toFixed(3) }; }
    }
    return best;
  }
  /** Keys held against an edge: the tile right ahead decides — sea from the deck means board, land from a boat means land. */
  function autoBoat(dx, dy, blockedX, blockedY) {
    // only a straight push into the edge counts; sliding along it with a diagonal never does
    if (dx && dy) { push = null; return; }
    const fx = Math.floor(me.x + 0.5), fy = Math.floor(me.y + 0.8);
    const ahead = [blockedX && [fx + Math.sign(dx), fy], blockedY && [fx, fy + Math.sign(dy)]].filter(Boolean);
    const spot = ahead.find(([x, y]) => (me.boat ? footOk(x, y) : water(x, y) && !footOk(x, y)));
    const t = now(), key = spot && `${me.boat}|${spot[0]},${spot[1]}`;
    if (!spot || t - boatAt < BOAT_COOLDOWN_MS) { push = null; return; }
    if (push?.key !== key) push = { key, since: t };
    if (t - push.since >= (me.boat ? PUSH_OUT_MS : PUSH_IN_MS)) { push = null; boatSwitch(spot, true); }
  }
  /** Move along one axis as far as the body fits (right up to a wall, not a step short); true if all the way. */
  function slide(ax, ay) {
    if (boxFree(me.x + ax, me.y + ay)) { me.x += ax; me.y += ay; return true; }
    let lo = 0, hi = 1;
    for (let i = 0; i < 6; i++) { const mid = (lo + hi) / 2; if (boxFree(me.x + ax * mid, me.y + ay * mid)) lo = mid; else hi = mid; }
    me.x += ax * lo; me.y += ay * lo; return false;
  }
  /** Pushing straight at a wall right next to a gap (a door, a corridor, the end of a wall): step sideways into it. */
  function cornerNudge(ax, ay, max) {
    for (let off = 0.05; off <= 0.4; off += 0.05) for (const sgn of [1, -1]) {
      const ox = ay ? sgn * off : 0, oy = ax ? sgn * off : 0;
      if (boxFree(me.x + ox, me.y + oy) && boxFree(me.x + ox + ax, me.y + oy + ay)) { slide(Math.sign(ox) * Math.min(max, off), Math.sign(oy) * Math.min(max, off)); return true; }
    }
    return false;
  }
  /** Along a travel path: straight by (ax, ay) if the body fits there (it does: the path was found for it); if it
   *  doesn't (something the path didn't know about), as far as each axis goes, and false. */
  function glide(ax, ay) {
    // Existing overlap (for example an older client's saved position): escape only while every step reduces the
    // overlap, avoids all new walls, and keeps the feet on tiles accepted by the server.
    if (recovering && !boxFits(C.solid, me.x, me.y, !!me.boat)) {
      if (!escapeClear(C.solid, me.x, me.y, me.x + ax, me.y + ay, !!me.boat)) return false;
      me.x += ax; me.y += ay; if (boxFree(me.x, me.y)) recovering = false; return true;
    }
    if (boxFree(me.x + ax, me.y + ay)) { me.x += ax; me.y += ay; recovering = false; return true; }
    slide(ax, 0); slide(0, ay); return false;
  }
  let stuck = 0; // seconds a travel path has been blocked (decays while it moves freely again)
  const STUCK_S = 0.25, REPLANS_MAX = 3;
  /** One frame: move by keys or along the travel path, then tell the office if it's time. */
  function step(dt) {
    let dx = 0, dy = 0;
    if (keys.has('arrowleft') || keys.has('a')) dx--; if (keys.has('arrowright') || keys.has('d')) dx++;
    if (keys.has('arrowup') || keys.has('w')) dy--; if (keys.has('arrowdown') || keys.has('s')) dy++;
    let power = 1; // the stick pushed part way: slower
    if (!dx && !dy && stick && keys.has(STICK)) { dx = stick.x; dy = stick.y; power = stick.power; }
    const byKeys = !!(dx || dy);
    let travelling = false; // on a click-to-go trip: the otter jogs upright (deck/pose.js)
    while (!byKeys && path?.length && path[0][0] === me.x && path[0][1] === me.y) path.shift();
    if (!byKeys && !planning && path?.length) {
      travelling = true;
      dx = path[0][0] - me.x; dy = path[0][1] - me.y; // any angle, straight at the next point
    }
    let moving = !!(dx || dy);
    if (moving) {
      const v = (me.boat ? BOAT_SPEED : SPEED) * power, len = Math.hypot(dx, dy), x0 = me.x, y0 = me.y;
      let okX = true, okY = true;
      if (byKeys) {
        // each axis on its own, so a diagonal into a wall slides along it; a straight push next to a gap slips into it
        const mx = (dx / len) * v * dt, my = (dy / len) * v * dt;
        okX = !mx || slide(mx, 0); okY = !my || slide(0, my);
        const straightX = Math.abs(my) < Math.abs(mx) * 0.15, straightY = Math.abs(mx) < Math.abs(my) * 0.15;
        if (!okX && straightX && Math.abs(me.y - y0) < 1e-9) cornerNudge(Math.sign(mx) * 0.05, 0, v * dt);
        else if (!okY && straightY && Math.abs(me.x - x0) < 1e-9) cornerNudge(0, Math.sign(my) * 0.05, v * dt);
      } else {
        // a frame's worth of the path, waypoint after waypoint (no overshoot, no slowing at corners), landing exactly
        // on each one, so the trip ends exactly where it was going
        let budget = v * dt, blocked = false;
        while (budget > 1e-9 && path.length) {
          const ex = path[0][0] - me.x, ey = path[0][1] - me.y, d = Math.hypot(ex, ey);
          if (d <= budget) { if (!glide(ex, ey)) { blocked = true; break; } me.x = path[0][0]; me.y = path[0][1]; path.shift(); budget -= d; }
          else { if (!glide((ex / d) * budget, (ey / d) * budget)) blocked = true; budget = 0; }
        }
        // blocked and not getting anywhere (something the path didn't know about): plan the rest again, a few times
        const slow = Math.hypot(me.x - x0, me.y - y0) < v * dt * 0.2;
        stuck = blocked && slow ? stuck + dt : Math.max(0, stuck - dt);
        if (stuck > STUCK_S && aim) {
          const n = replans + 1, a = aim; stuck = 0;
          if (n > REPLANS_MAX) { path = null; plan = []; toast(word('map.move.blocked')); }
          else continueIntent(async (ctx) => { await walkTo(a.tx, a.ty, a.exact, a.point, a.r, ctx); guard(ctx); replans = n;
            if (!path) { plan = []; toast(word('map.move.blocked')); return false; } return true; });
        } else if (!path.length) { path = null; if (plan.length) continueIntent(nextLeg); }
      }
      // face the way I'm going; on a trip, the last little nudge onto the exact spot clicked (a fraction of a tile,
      // often sideways or back) doesn't turn me, so I end up facing the way I walked toward the click
      // (8-way: the way I actually went, so a diagonal key into a wall that slides me along it faces along the wall)
      if (byKeys || len > 0.35) {
        const ax = me.x - x0, ay = me.y - y0, moved = Math.hypot(ax, ay) > v * dt * 0.2;
        me.dir = dir4Of(dx, dy); me.dir8 = moved ? dir8Of(ax, ay, dir8(me)) : dir8Of(dx, dy);
        me.dir16 = moved ? dir16Of(ax, ay, dir16(me)) : dir16Of(dx, dy); if (!near16(me.dir16, me.dir8 * 2)) me.dir16 = me.dir8 * 2;
      }
      if (byKeys) autoBoat(dx, dy, !okX && !!dx, !okY && !!dy);
      // a step the page won't take (into a 会议室 while in a 通话: public/call.js mayEnter): back where I was, standing
      // at the door, the trip dropped (the gate asks what to do). Judged where the office will see it: the position as
      // sendPos sends it (2 decimals), since a step stopping within 0.005 of a room's top or left edge rounds onto it
      if ((me.x !== x0 || me.y !== y0) && !mayEnter(+x0.toFixed(2), +y0.toFixed(2), +me.x.toFixed(2), +me.y.toFixed(2))) { me.x = x0; me.y = y0; path = null; plan = []; moving = false; }
    } else {
      push = null;
      if (path && !path.length) { path = null; if (plan.length) continueIntent(nextLeg); } // already where the path ends
    }
    // positions go out at most every 100 ms while moving; the spot where I stop always goes out, even if late
    const t = now();
    const unsent = +me.x.toFixed(2) !== sentAt.x || +me.y.toFixed(2) !== sentAt.y || moving !== sentAt.moving || !!me.boat !== sentAt.boat || me.dir !== sentAt.dir || dir8(me) !== sentAt.dir8 || dir16(me) !== sentAt.dir16;
    if (t - lastSent > 100 && unsent) { sendPos(moving); lastSent = t; }
    me.moving = moving;
    me.running = moving && travelling && !me.boat;
  }

  /** A click (or tap) on the map at world point (wx, wy), in tiles: go there. A tile I can stand on in what I'd end up
   *  in (deck, beach or sea) is gone to exactly, finishing on the spot clicked; anything else (a wall,
   *  a roof) as near as I can get. Land clicked from a boat means sail to the shore, climb out and walk on. */
  function clickAt(wx, wy) {
    const tx = Math.floor(wx), ty = Math.floor(wy);
    const free = footOk(tx, ty) || water(tx, ty);
    // turn toward the click straight away (and stay turned if it's too close to walk to)
    const fx = wx - 0.5 - me.x, fy = wy - 0.8 - me.y;
    if (Math.hypot(fx, fy) > 0.05) { me.dir = dir4Of(fx, fy); me.dir8 = dir8Of(fx, fy); me.dir16 = dir16Of(fx, fy); if (!near16(me.dir16, me.dir8 * 2)) me.dir16 = me.dir8 * 2; }
    return startIntent('travel', [tx, ty, free, free ? [wx - 0.5, wy - 0.8] : null, true]);
  }

  return {
    keys, water, footOk, zoneAt: (x, y) => C.zoneAt(x, y),
    /** Use another collider (same interface as collide.js collider()). */
    setCollider(c) { C = c; },
    // Planning methods resolve true for a route, false for no route/failure, and null when a newer intent cancels it.
    // A map edit replans the same intent and keeps its promise; planning() stays true while no route is installed.
    step: withMe(step), travelTo: (tx, ty, exact, point, nearest = false) => startIntent('travel', [tx, ty, exact, point, nearest]),
    clickAt: (wx, wy) => { me = getMe(); return me ? clickAt(wx, wy) : Promise.resolve(false); },
    walkTo: (tx, ty, exact, point, r = 1) => startIntent('walk', [tx, ty, exact, point, r], false),
    toggleBoat: withMe(toggleBoat), boatSwitch: withMe(boatSwitch),
    boxFree: withMe(boxFree),
    /** The on-screen stick's push (deck/stick.js): direction (x, y), any length, and power 0..1 of walking speed;
     *  (0, 0) or power 0 lets go. Walks only while keys has STICK. */
    setStick(x, y, power = 1) { stick = (x || y) && power > 0 ? { x, y, power: Math.min(1, power) } : null; },
    /** Forget any travel (keys pressed, the office put me back). */
    clear() { cancelIntent(); path = null; plan = []; recovering = false; },
    /** How many trips have been asked for (each travelTo: a double click, 走过去, sitting down...). */
    trips: () => trips, intentVersion: () => generation,
    /** The map changed under a trip (an edit saved, a preview): find the way again from here (this leg; the legs
     *  after it are planned when they come), or stop if there's none now. Refresh the collider first. */
    replan() {
      const previous = intent;
      if (!previous || (!path && !planning && !plan.length)) { cancelIntent(); path = null; plan = []; recovering = false; return Promise.resolve(false); }
      // This is still the same user intent: keep its result promise and follow-on actions alive, but cancel the old
      // map's native work. The running operation observes the new revision and restarts on the new geometry.
      previous.revision++;
      const old = previous.controller; previous.controller = new AbortController(); old.abort();
      path = null; plan = []; recovering = false;
      if (planning) return previous.promise;
      return beginOperation(previous, () => previous.kind === 'travel' ? travelTo(...previous.args, previous) : walkTo(...previous.args, previous));
    },
    /** Whatever was being pushed against no longer counts (keys released all at once, e.g. chat opened). */
    unpush() { push = null; },
    path: () => path, plan: () => plan, planning: () => planning,
  };
}
